// components/ORFTypeSelection.jsx
import React, { useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Card, CardBody, CardHeader, Button, Tooltip } from "reactstrap";

const ORFTypeSelection = ({ selectedType, onTypeChange }) => {
  const [tooltipOpen, setTooltipOpen] = useState({});

  const toggle = (id) => {
    setTooltipOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const orfTypes = [
    {
      value: "uorf",
      label: "Upstream open reading frame",
      tooltip:
        "Search for open reading frames contained entirely within the 5' UTR/5' Leader, no part of the open reading frame overlaps with the annotated CDS.",
    },
    {
      value: "ouorf",
      label: "Overlapping upstream open reading",
      tooltip:
        "Search for open reading frames that start within the 5' UTR/5' Leader and end within the annotated CDS.",
    },
    {
      value: "extension",
      label: "N-terminal extension",
      tooltip:
        "Search for start codons within the 5' UTR/5' Leader in the same frame as the annotated CDS where there is no stop codon between the potential start and the annotated CDS start. The stop position in this case will be the annotated CDS start.",
    },
    {
      value: "cds",
      label: "Annotated coding region",
      tooltip: "Search for annotated CDS regions.",
    },
    {
      value: "nested",
      label: "Nested open reading frame",
      tooltip:
        "Search for open reading frames contained entirely within the annotated CDS. Both the start and stop codon are within the CDS and in a different frame to the annotated CDS.",
    },
    {
      value: "odorf",
      label: "Overlapping downstream open reading",
      tooltip:
        "Search for open reading frames that start within the annotated CDS and end within the 3' UTR/3' Trailer.",
    },
    {
      value: "dorf",
      label: "Downstream open reading frame",
      tooltip:
        "Search for open reading frames contained entirely within the 3' UTR/3' Trailer, no part of the open reading frame overlaps with the annotated CDS.",
    },
    {
      value: "noncoding",
      label: "Non coding",
      tooltip:
        "Search for open reading frames on transcripts annotated as non-coding.",
    },
    {
      value: "readthrough",
      label: "Readthrough",
      tooltip:
        "Search between the annotated CDS stop and the next stop codon in the same frame as the CDS.",
    },
    {
      value: "plusone",
      label: "Plus one frameshift",
      tooltip:
        "Search between the annotated CDS stop and the next stop codon in the +1 frame relative to the CDS frame.",
    },
    {
      value: "minusone",
      label: "Minus one frameshift",
      tooltip:
        "Search between the annotated CDS stop and the next stop codon in the -1 frame relative to the CDS frame.",
    },
  ];

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "4px",
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <h5
            className="mb-0 fw-semibold d-flex align-items-center"
            style={{ color: "#1a6d51" }}
          >
            <i className="ri-settings-3-line me-2" />
            ORF Type Selection
          </h5>
        </div>
      </CardHeader>
      <CardBody>
        <div className="d-flex flex-wrap gap-2">
          {orfTypes.map((type) => (
            <div key={type.value}>
              <Button
                id={`button-${type.value}`}
                color={
                  selectedType === type.value ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => onTypeChange(type.value)}
                style={{
                  fontWeight: "500",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {type.label}
                <AiOutlineInfoCircle
                  title={type.tooltip}
                  style={{
                    marginLeft: "4px",
                    cursor: "help",
                    color: selectedType === type.value ? "white" : "#6B7280",
                    fontSize: "16px",
                    verticalAlign: "middle",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                />
              </Button>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

export default ORFTypeSelection;
